import React from 'react';
import { Checkbox } from './Checkbox';
import { twMerge } from 'tailwind-merge';

interface TableProps {
  columns: {
    title: string;
    key: string;
    render?: (value: any, record: any, rowIndex?: number) => React.ReactNode;
    width?: string | number;
    ellipsis?: boolean;
    className?: string;
    style?: React.CSSProperties;
    onHeaderCell?: () => { className?: string; style?: any };
    onCell?: (record: any) => { className?: string; style?: any };
  }[];
  dataSource: any[];
  rowKey?: string;
  onRowClick?: (record: any) => void;
  loading?: boolean;
  selection?: {
    selectedRowKeys: any[];
    onChange: (keys: any[]) => void;
  };
  minWidth?: string;
  className?: string;
  tableClassName?: string;
}

export const Table = ({ columns, dataSource, rowKey = 'id', onRowClick, loading, selection, minWidth, className, tableClassName }: TableProps) => {
  const isAllSelected = dataSource.length > 0 && selection?.selectedRowKeys.length === dataSource.length;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isScrolledToRight, setIsScrolledToRight] = React.useState(true);

  const checkScroll = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const scrolledToRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 5;
    setIsScrolledToRight(scrolledToRight);
  }, []);

  const checkStickyRight = React.useCallback((col: any, cellClassName: string, cellStyle: any) => {
    return (
      col.className?.includes('right-0') ||
      cellClassName.includes('right-0') ||
      col.style?.right === 0 ||
      col.style?.right === '0px' ||
      cellStyle?.right === 0 ||
      cellStyle?.right === '0px'
    );
  }, []);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let active = true;
    checkScroll();

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (active) checkScroll();
      });
    });
    // Only observe the container element, NOT the inner table element (el.firstElementChild).
    // Observing only the container prevents loops caused by cell layout changes in table rendering.
    resizeObserver.observe(el);

    return () => {
      active = false;
      resizeObserver.disconnect();
    };
  }, [dataSource, loading, checkScroll]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      selection?.onChange(dataSource.map(item => item[rowKey]));
    } else {
      selection?.onChange([]);
    }
  };

  const handleSelectRow = (key: any, checked: boolean) => {
    if (checked) {
      selection?.onChange([...selection.selectedRowKeys, key]);
    } else {
      selection?.onChange(selection.selectedRowKeys.filter(k => k !== key));
    }
  };

  return (
    <div
      ref={containerRef}
      onScroll={checkScroll}
      className={twMerge("relative overflow-x-auto custom-scrollbar rounded-xl border border-slate-100 shadow-sm bg-white", className)}
    >
      <table
        className={twMerge("w-full text-left border-separate border-spacing-0", tableClassName)}
        style={{ minWidth: minWidth || '100%' }}
      >
        <thead>
          <tr className="bg-slate-50/80 backdrop-blur-md">
            {selection && (
              <th className="sticky top-0 md:left-0 z-10 md:z-20 px-4 py-2.5 border-b border-slate-100 rounded-tl-xl w-10 bg-slate-50/80 backdrop-blur-md">
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            )}
            {columns.map((col, idx) => {
              const headerCellProps = col.onHeaderCell ? col.onHeaderCell() : {};
              const headerClassName = headerCellProps.className || '';
              const headerStyle = headerCellProps.style || {};

              const isStickyRight = checkStickyRight(col, headerClassName, headerStyle);

              return (
                <th
                  key={col.key}
                  className={twMerge(
                    "px-3 py-3 sm:px-5 sm:py-3.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide border-b border-slate-100",
                    !selection && idx === 0 && "md:sticky md:left-0 md:z-20 bg-slate-50",
                    !selection && idx === 0 && "rounded-tl-xl",
                    selection && idx === 0 && "md:sticky md:left-[40px] md:z-20 bg-slate-50",
                    selection && idx === 0 && "rounded-tl-xl",
                    idx === columns.length - 1 && "rounded-tr-xl",
                    col.className,
                    headerClassName,
                  )}
                  style={{ width: col.width, ...col.style, ...headerStyle }}
                >
                  {col.title}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="bg-white">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="animate-pulse">
                {selection && <td className="px-3 py-3 border-b border-slate-100" />}
                {columns.map((col, j) => (
                  <td key={j} className="px-3 py-3 border-b border-slate-100">
                    <div className="h-4 bg-slate-100 rounded w-3/4"></div>
                  </td>
                ))}
              </tr>
            ))
          ) : dataSource.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selection ? 1 : 0)} className="px-4 py-8 text-center text-slate-400 text-sm">
                No data available
              </td>
            </tr>
          ) : (
            dataSource.map((record, index) => {
              const key = record[rowKey] || index;
              const isSelected = selection?.selectedRowKeys.includes(key);

              return (
                <tr
                  key={key}
                  data-selected={isSelected}
                  className="group transition-all duration-200"
                >
                  {selection && (
                    <td className={twMerge(
                      "md:sticky md:left-0 md:z-20 px-3 py-2.5 sm:px-4 border-b border-slate-200 group-last:border-none transition-colors duration-200",
                      isSelected ? "bg-[#8dc63f]/5" : "bg-white group-hover:bg-slate-50"
                    )}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleSelectRow(key, e.target.checked)}
                      />
                    </td>
                  )}
                  {columns.map((col, idx) => {
                    const cellProps = col.onCell ? col.onCell(record) : {};
                    const cellClassName = cellProps.className || '';
                    const cellStyle = cellProps.style || {};

                    const isStickyRight = checkStickyRight(col, cellClassName, cellStyle);

                    return (
                      <td
                        key={col.key}
                        onClick={() => onRowClick && onRowClick(record)}
                        className={twMerge(
                          "px-3 py-3 sm:px-5 sm:py-4 text-left text-xs md:text-sm text-slate-800 border-b border-slate-200 group-last:border-none transition-colors duration-200",
                          isSelected ? "bg-[#8dc63f]/5" : "bg-white group-hover:bg-slate-50",
                          onRowClick && "cursor-pointer",
                          col.className,
                          cellClassName,
                          idx === 0 && selection && "md:sticky md:left-[40px] md:z-20",
                          !selection && idx === 0 && "md:sticky md:left-0 md:z-20",
                        )}
                        style={{ ...col.style, ...cellStyle }}
                      >
                        {col.render ? col.render(record[col.key], record) : record[col.key]}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};



